import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useLanguage } from '../i18n/LanguageContext';
import { getProjectBySlug, projects } from '../data/projects';

const { FiArrowLeft, FiArrowUpRight, FiCheck } = FiIcons;

const ProjectDetail = () => {
  const { slug } = useParams();
  const { t, localize } = useLanguage();
  const baseProject = getProjectBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!baseProject) {
    return <Navigate to="/" replace />;
  }

  const project = localize(baseProject);
  const projectIndex = projects.findIndex((item) => item.slug === slug);
  const nextProject = localize(projects[(projectIndex + 1) % projects.length]);

  return (
    <main className="relative min-h-screen bg-[#050505] text-white overflow-hidden">
      <div
        className={`absolute inset-x-0 top-0 h-[560px] bg-gradient-to-b ${project.accent} pointer-events-none`}
      />
      <div className="absolute top-0 right-0 w-[55vw] h-[55vw] rounded-full border border-white/5 -translate-y-1/2 translate-x-1/3 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-8 md:pt-12 pb-24">
        <nav className="flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-gray-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            {t('allProjects')}
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <span className="text-white font-black text-2xl tracking-tighter">
              DB.
            </span>
          </div>
        </nav>

        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="pt-24 md:pt-36 pb-20 md:pb-28"
        >
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <span className="px-3 py-1 rounded-full border border-white/15 bg-white/5 text-xs uppercase tracking-widest text-gray-300">
              {project.category}
            </span>
            <span className="text-xs uppercase tracking-widest text-emerald-400">
              {project.status}
            </span>
          </div>
          <h1 className="max-w-5xl text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black tracking-[-0.06em] leading-[0.9]">
            {project.title}
          </h1>
          <p className="max-w-3xl mt-10 text-xl md:text-2xl leading-relaxed text-gray-400 font-light">
            {project.summary}
          </p>
        </motion.header>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 border-t border-white/10 pt-14">
          <motion.aside
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="lg:col-span-4"
          >
            <dl className="space-y-8">
              <div>
                <dt className="text-xs uppercase tracking-widest text-gray-600 mb-2">
                  {t('role')}
                </dt>
                <dd className="text-lg text-gray-200">{project.role}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-gray-600 mb-3">
                  {t('technologies')}
                </dt>
                <dd className="flex flex-wrap gap-2">
                  {project.technologies.map((technology) => (
                    <span
                      key={technology}
                      className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-400"
                    >
                      {technology}
                    </span>
                  ))}
                </dd>
              </div>
              {project.website && (
                <div>
                  <dt className="text-xs uppercase tracking-widest text-gray-600 mb-3">
                    {t('website')}
                  </dt>
                  <dd>
                    <a
                      href={project.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-white hover:text-emerald-400 transition-colors"
                    >
                      {t('visit')}
                      <SafeIcon icon={FiArrowUpRight} className="w-4 h-4" />
                    </a>
                  </dd>
                </div>
              )}
              {project.storeLinks && (
                <div>
                  <dt className="text-xs uppercase tracking-widest text-gray-600 mb-3">
                    {t('download')}
                  </dt>
                  <dd className="space-y-2">
                    {project.storeLinks.map((store) => (
                      <a
                        key={store.label}
                        href={store.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-white hover:text-emerald-400 transition-colors"
                      >
                        {store.label}
                        <SafeIcon icon={FiArrowUpRight} className="w-4 h-4" />
                      </a>
                    ))}
                    {project.storeNote && (
                      <p className="text-sm text-gray-500">{project.storeNote}</p>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </motion.aside>

          <motion.article
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="lg:col-span-8"
          >
            <p className="text-2xl md:text-4xl leading-snug text-gray-200 font-light">
              {project.description}
            </p>

            <div className="mt-16">
              <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-8">
                {t('services')}
              </h2>
              <ul className="grid sm:grid-cols-2 gap-4">
                {project.services.map((service) => (
                  <li
                    key={service}
                    className="flex items-start gap-3 p-5 rounded-xl border border-white/10 bg-white/[0.025] text-gray-300"
                  >
                    <SafeIcon
                      icon={FiCheck}
                      className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5"
                    />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.article>
        </div>

        <footer className="mt-28 pt-10 border-t border-white/10">
          <span className="block text-xs uppercase tracking-widest text-gray-600 mb-4">
            {t('next')}
          </span>
          <Link
            to={`/projekte/${nextProject.slug}`}
            className="group flex items-end justify-between gap-6"
          >
            <span className="text-3xl md:text-6xl font-bold tracking-tight text-gray-300 group-hover:text-white transition-colors">
              {nextProject.title}
            </span>
            <SafeIcon
              icon={FiArrowUpRight}
              className="w-8 h-8 md:w-12 md:h-12 text-gray-600 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
            />
          </Link>
        </footer>
      </div>
    </main>
  );
};

export default ProjectDetail;
